import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
  Plus, 
  ChevronDown, 
  ChevronUp,
  Calendar,
  Users,
  User,
  Clock,
  UserCheck,
  ArrowUp,
  ArrowDown,
  Edit2,
  Trash2,
  X,
  Check,
  Download,
  RefreshCw
} from 'lucide-react';
import { protocolsApi, sectionsApi, Section, Task } from '../lib/api';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { StatusBadge, PriorityBadge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';
import { useAuthStore } from '../store/auth';
import { isTaskOverdue } from '../utils/overdue';

export function ProtocolPage() {
  const { protocolId } = useParams<{ protocolId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hapticFeedback, showAlert } = useTelegram();

  const [isAddingSection, setIsAddingSection] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState('');
  
  const currentUser = useAuthStore((state) => state.user);

  const { data: protocol, isLoading, error } = useQuery({
    queryKey: ['protocol', protocolId],
    queryFn: async () => {
      try {
        const data = await protocolsApi.get(protocolId!);
        return data;
      } catch (error: any) {
        throw error;
      }
    },
    enabled: !!protocolId,
  });

  const createSectionMutation = useMutation({
    mutationFn: sectionsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] });
      setIsAddingSection(false);
      setNewSectionTitle('');
      hapticFeedback('success');
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const updateSectionMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      sectionsApi.update(id, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] });
      setEditingSectionId(null);
      setEditingSectionTitle('');
      hapticFeedback('success');
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const deleteSectionMutation = useMutation({
    mutationFn: sectionsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] });
      hapticFeedback('success');
      showAlert('Раздел удалён');
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const initSectionsMutation = useMutation({
    mutationFn: () => sectionsApi.initDefault(protocolId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] });
      hapticFeedback('success');
      showAlert('Стандартные разделы созданы!');
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const exportMutation = useMutation({
    mutationFn: () => protocolsApi.export(protocolId!),
    onSuccess: (data) => {
      hapticFeedback('success');
      showAlert(`✅ ${data.message}`);
      // Открываем таблицу в новом окне
      if (data.spreadsheetUrl) {
        window.open(data.spreadsheetUrl, '_blank');
      }
    },
    onError: (err: Error) => {
      showAlert(`❌ Ошибка экспорта: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => protocolsApi.sync(protocolId!),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] });
      hapticFeedback('success');
      showAlert(`✅ ${data.message}\n\nОбновлено: ${data.stats.updated}\nПропущено: ${data.stats.skipped}`);
    },
    onError: (err: Error) => {
      showAlert(`❌ Ошибка синхронизации: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const reorderSectionsMutation = useMutation({
    mutationFn: sectionsApi.reorder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocol', protocolId] });
      hapticFeedback('success');
    },
    onError: (err: Error) => {
      showAlert(`Ошибка: ${err.message}`);
      hapticFeedback('error');
    },
  });

  const handleMoveSection = (sectionId: string, direction: 'up' | 'down') => {
    if (!protocol?.sections) return;
    
    const sections = [...protocol.sections].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    const currentIndex = sections.findIndex(s => s.id === sectionId);
    
    if (currentIndex === -1) return;
    
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === sections.length - 1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    [sections[currentIndex], sections[newIndex]] = [sections[newIndex], sections[currentIndex]];
    
    const reordered = sections.map((s, i) => ({
      id: s.id,
      sortOrder: i + 1,
    }));
    
    reorderSectionsMutation.mutate(reordered);
  };

  const toggleSection = (sectionId: string) => {
    hapticFeedback('light');
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const handleAddSection = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSectionTitle.trim() || !protocolId || !protocol) return;

    const nextNumber = (protocol.sections?.length || 0) + 1;
    
    createSectionMutation.mutate({
      protocolId,
      number: nextNumber,
      title: newSectionTitle.trim(),
    });
  };

  const canEdit = protocol?.userRole && ['ADMIN', 'MANAGER'].includes(protocol.userRole);

  // Подсчёт статистики
  const stats = protocol?.sections?.reduce(
    (acc, section) => {
      (section.tasks || []).forEach((task) => {
        acc.total++;
        if (task.status === 'COMPLETED') acc.completed++;
        if (isTaskOverdue(task)) {
          acc.overdue++;
        }
      });
      return acc;
    },
    { total: 0, completed: 0, overdue: 0 }
  ) || { total: 0, completed: 0, overdue: 0 };

  // Фильтрация задач
  const filterTasks = (section: Section) => {
    let tasks = section.tasks || [];
    
    // Фильтр "только мои"
    if (showOnlyMine && currentUser) {
      const beforeFilter = tasks.length;
      tasks = tasks.filter((task) =>
        task.assignees?.some((a) => a.id === currentUser.id)
      );
    }
    
    // Фильтр по статусу
    if (statusFilter) {
      const beforeFilter = tasks.length;
      tasks = tasks.filter((task) => {
        if (statusFilter === 'OVERDUE') {
          return isTaskOverdue(task);
        }
        return task.status === statusFilter;
      });
    }
    return tasks;
  };
  
  // Подсчёт моих задач
  const myTasksCount = protocol?.sections?.reduce((acc, section) => {
    return acc + (section.tasks?.filter((task) =>
      task.assignees?.some((a) => a.id === currentUser?.id)
    ).length || 0);
  }, 0) || 0;

  // Проверка просрочки с учетом времени дня
  const isOverdue = (task: Task) => isTaskOverdue(task);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!protocol) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-500">Протокол не найден</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-24">
      {/* Заголовок */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-4">
        <h1 className="text-xl font-bold">{protocol.title}</h1>
        {protocol.theme && (
          <p className="text-sm text-white/80 mt-1">{protocol.theme}</p>
        )}
        <div className="flex items-center gap-4 mt-3 text-sm text-white/80">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {format(new Date(protocol.meetingDate), 'd MMMM yyyy', { locale: ru })}
          </span>
          {protocol.chairman && (
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {protocol.chairman}
            </span>
          )}
        </div>

        {/* Статистика */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
            Всего: {stats.total}
          </span>
          <span className="bg-green-400/30 px-3 py-1 rounded-full text-sm font-medium">
            ✓ {stats.completed}
          </span>
          {stats.overdue > 0 && (
            <span className="bg-red-400/30 px-3 py-1 rounded-full text-sm font-medium">
              ⚠ {stats.overdue} просрочено
            </span>
          )}
        </div>

      </div>

      {/* Кнопки экспорта и синхронизации - всегда видимые, но с проверкой прав на бэкенде */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              hapticFeedback('medium');
              exportMutation.mutate();
            }}
            disabled={exportMutation.isPending}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            <Download className="w-5 h-5" />
            {exportMutation.isPending ? 'Экспорт...' : '↓ Экспорт в Sheets'}
          </button>
          <button
            onClick={() => {
              hapticFeedback('medium');
              syncMutation.mutate();
            }}
            disabled={syncMutation.isPending}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            <RefreshCw className={`w-5 h-5 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
            {syncMutation.isPending ? 'Синхронизация...' : 'Синхронизировать'}
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 space-y-2">
        {/* Переключатель "Только мои" - только для ADMIN, MANAGER и OBSERVER */}
        {myTasksCount > 0 && protocol.userRole && ['ADMIN', 'MANAGER', 'OBSERVER'].includes(protocol.userRole) && (
          <button
            onClick={() => {
              hapticFeedback('medium');
              setShowOnlyMine(!showOnlyMine);
            }}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              showOnlyMine 
                ? 'bg-green-500 text-white' 
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}
          >
            <UserCheck className="w-4 h-4" />
            Только мои задачи ({myTasksCount})
          </button>
        )}
        
        {/* Фильтры по статусу */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {[
            { value: null, label: 'Все' },
            { value: 'IN_PROGRESS', label: 'В процессе' },
            { value: 'COMPLETED', label: 'Выполнено' },
            { value: 'OVERDUE', label: 'Просрочено' },
            { value: 'PAUSED', label: 'Приостановлено' },
          ].map((filter) => (
            <button
              key={filter.value || 'all'}
              onClick={() => {
                hapticFeedback('light');
                setStatusFilter(filter.value);
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === filter.value 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Разделы и задачи */}
      <div className="p-4 space-y-4">
        {protocol.sections?.map((section) => {
          const filteredTasks = filterTasks(section);
          const isCollapsed = collapsedSections.has(section.id);
          
          return (
            <Card key={section.id} className="overflow-hidden">
              {/* Заголовок раздела - синий */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 flex items-center gap-2">
                {editingSectionId === section.id ? (
                  // Режим редактирования
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      type="text"
                      value={editingSectionTitle}
                      onChange={(e) => setEditingSectionTitle(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg bg-white/20 text-white placeholder:text-white/50 border border-white/30 focus:outline-none focus:border-white"
                      placeholder="Название раздела"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && editingSectionTitle.trim()) {
                          updateSectionMutation.mutate({
                            id: section.id,
                            title: editingSectionTitle.trim(),
                          });
                        }
                        if (e.key === 'Escape') {
                          setEditingSectionId(null);
                          setEditingSectionTitle('');
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        if (editingSectionTitle.trim()) {
                          updateSectionMutation.mutate({
                            id: section.id,
                            title: editingSectionTitle.trim(),
                          });
                        }
                      }}
                      disabled={!editingSectionTitle.trim() || updateSectionMutation.isPending}
                      className="p-2 hover:bg-white/20 rounded disabled:opacity-30"
                      title="Сохранить"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingSectionId(null);
                        setEditingSectionTitle('');
                      }}
                      className="p-2 hover:bg-white/20 rounded"
                      title="Отмена"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  // Обычный режим
                  <>
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="flex-1 flex items-center justify-between text-left"
                    >
                      <div className="flex-1">
                        <span className="font-semibold">
                          {section.number}. {section.title}
                        </span>
                        <span className="text-sm text-white/70 ml-2">
                          ({(section.tasks || []).length} задач)
                        </span>
                      </div>
                      {isCollapsed ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronUp className="w-5 h-5" />
                      )}
                    </button>
                    {canEdit && protocol.sections && (
                      <div className="flex gap-1">
                        {/* Редактировать */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            hapticFeedback('light');
                            setEditingSectionId(section.id);
                            setEditingSectionTitle(section.title);
                          }}
                          className="p-2 hover:bg-white/20 rounded"
                          title="Редактировать"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {/* Удалить */}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            hapticFeedback('medium');
                            const confirmed = confirm(
                              `Удалить раздел "${section.title}"?\n\nВсе задачи в этом разделе также будут удалены. Это действие необратимо.`
                            );
                            if (confirmed) {
                              deleteSectionMutation.mutate(section.id);
                            }
                          }}
                          disabled={deleteSectionMutation.isPending}
                          className="p-2 hover:bg-red-500/30 rounded disabled:opacity-30"
                          title="Удалить"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {/* Переместить вверх/вниз */}
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              hapticFeedback('light');
                              handleMoveSection(section.id, 'up');
                            }}
                            disabled={reorderSectionsMutation.isPending || (protocol.sections.findIndex(s => s.id === section.id) === 0)}
                            className="p-1 hover:bg-white/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Переместить вверх"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              hapticFeedback('light');
                              handleMoveSection(section.id, 'down');
                            }}
                            disabled={reorderSectionsMutation.isPending || (protocol.sections.findIndex(s => s.id === section.id) === protocol.sections.length - 1)}
                            className="p-1 hover:bg-white/20 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Переместить вниз"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Задачи раздела - всегда видны если не свернуто */}
              {!isCollapsed && (
                <CardContent noPadding>
                  {filteredTasks.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-6">
                      Нет задач{statusFilter ? ' с выбранным фильтром' : ''}
                    </p>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {filteredTasks.map((task) => (
                        <button
                          key={task.id}
                          onClick={() => {
                            hapticFeedback('light');
                            navigate(`/tasks/${task.id}`);
                          }}
                          className={`w-full text-left p-4 hover:bg-gray-50 transition-colors border-l-4 ${
                            task.priority === 'CRITICAL' ? 'border-l-red-500' :
                            task.priority === 'HIGH' ? 'border-l-orange-500' :
                            'border-l-green-500'
                          }`}
                        >
                          {/* Код и статусы */}
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="text-sm font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                              {task.code}
                            </span>
                            <div className="flex items-center gap-2">
                              <StatusBadge status={task.status} isOverdue={isOverdue(task)} />
                              <PriorityBadge priority={task.priority} />
                            </div>
                          </div>

                          {/* Текст задачи */}
                          <p className="text-gray-900 font-medium mb-2 line-clamp-2">
                            {task.text}
                          </p>

                          {/* Мета: ответственный и срок */}
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            {task.assignees && task.assignees.length > 0 && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {task.assignees.map(a => 
                                  `${a.lastName || ''} ${a.firstName}`
                                ).join(', ')}
                              </span>
                            )}
                            {task.dueDate && (
                              <span className={`flex items-center gap-1 ${isOverdue(task) ? 'text-red-500 font-medium' : ''}`}>
                                <Clock className="w-3 h-3" />
                                {format(new Date(task.dueDate), 'dd.MM.yyyy')}
                                {isOverdue(task) && ' !'}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Кнопка добавления задачи */}
                  {canEdit && (
                    <div className="p-3 bg-gray-50 border-t border-gray-100">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          hapticFeedback('light');
                          navigate(`/protocols/${protocolId}/tasks/new?sectionId=${section.id}`);
                        }}
                        className="w-full justify-center gap-1 text-blue-600"
                      >
                        <Plus className="w-4 h-4" />
                        Добавить задачу
                      </Button>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

        {/* Если мало разделов - предложить создать стандартные */}
        {protocol.sections && protocol.sections.length < 10 && canEdit && (
          <Card className="p-4 bg-blue-50 border-blue-200">
            <p className="text-sm text-blue-800 mb-3">
              💡 Создать 7 стандартных разделов протокола?
            </p>
            <Button
              onClick={() => initSectionsMutation.mutate()}
              isLoading={initSectionsMutation.isPending}
              size="sm"
            >
              Создать стандартные разделы
            </Button>
          </Card>
        )}

        {/* Если нет разделов */}
        {(!protocol.sections || protocol.sections.length === 0) && (
          <div className="text-center py-12">
            <p className="text-gray-400 mb-4">Разделы ещё не созданы</p>
            {canEdit && (
              <Button onClick={() => setIsAddingSection(true)}>
                Добавить первый раздел
              </Button>
            )}
          </div>
        )}

        {/* Добавление раздела */}
        {canEdit && protocol.sections && protocol.sections.length > 0 && (
          <>
            {isAddingSection ? (
              <Card className="p-4 animate-slide-down">
                <form onSubmit={handleAddSection} className="space-y-3">
                  <Input
                    placeholder="Название раздела"
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      isLoading={createSectionMutation.isPending}
                      disabled={!newSectionTitle.trim()}
                      className="flex-1"
                    >
                      Добавить
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setIsAddingSection(false);
                        setNewSectionTitle('');
                      }}
                    >
                      Отмена
                    </Button>
                  </div>
                </form>
              </Card>
            ) : (
              <Button
                variant="secondary"
                onClick={() => setIsAddingSection(true)}
                className="w-full gap-1"
              >
                <Plus className="w-4 h-4" />
                Добавить раздел
              </Button>
            )}
          </>
        )}
      </div>

      {/* Плавающая кнопка добавления задачи */}
      {canEdit && protocol.sections && protocol.sections.length > 0 && (
        <div className="fixed bottom-20 right-4">
          <Button
            onClick={() => {
              hapticFeedback('medium');
              navigate(`/protocols/${protocolId}/tasks/new`);
            }}
            className="rounded-full w-14 h-14 p-0 shadow-lg btn-primary"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </div>
      )}
    </div>
  );
}
